<!--
 * @Author: yuhaiqin@wps.cn
 * @Description: 工作区顶部信息 - 工作区套餐
-->
<template>
  <div class="left">
    <div class="left-label">工作区套餐：</div>

    <!-- 当前工作区创建者才允许付费 -->
    <template v-if="(teamStore.isWorkbenchOwner || teamStore.isWorkbenchManager) && !teamStore.isTeamUserDisabled">
      <!-- 免费版 -->
      <base-button v-if="isFree || isTrial" class="workbench-btn buy-btn" type="default" @click="goPay(1, 'buy_plat')">
        立即升级
      </base-button>

      <!-- 付费且非最高版本 -->
      <template v-else-if="!teamRights?.is_top_member">
        <base-button class="workbench-btn buy-btn_small btn-right" type="default" @click="goPay(3, 'upgrade_plat')">
          升级
        </base-button>
        <base-button class="workbench-btn buy-btn_small" type="default" @click="goPay(2, 'renew_plat')">
          续费
        </base-button>
      </template>

      <!-- 最高版本 -->
      <base-button v-else class="workbench-btn buy-btn" type="default" @click="goPay(2, 'renew_plat')">
        立即续费
      </base-button>
    </template>

    <!-- 没有权限占位 -->
    <div v-else style="height: 20px"></div>
  </div>

  <!-- 权益详情 -->
  <div class="right">
    <template v-if="teamStore.isInitStaticsData">
      <!-- logo 以及套餐名 -->
      <div class="pay">
        <img class="pay-logo" :src="memberConfig.logo" />
        <div class="pay-text" :style="{ color: memberConfig.color }">
          {{ memberTitle }}
        </div>
      </div>

      <!-- 免费版营销 -->
      <div v-if="isFree" class="ad-text">
        升级后立享60+功能特权<span class="privilege-detail" @click="goPrivilegeDetail">特权详情</span>
      </div>

      <!-- 权益到期提醒 -->
      <div v-else class="ad-text">
        <IconMyIconTips v-if="memberExpire.days <= 7" class="tips"></IconMyIconTips>{{ memberExpire.str }}
      </div>
    </template>
    <!-- 骨架屏 -->
    <div v-else class="skeleton-main">
      <workbench-info-skeleton :width="208" :height="22"></workbench-info-skeleton>
      <workbench-info-skeleton :width="208" :height="22"></workbench-info-skeleton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { usePTeamStore } from '@/store';
import BaseButton from '@/components/base-button.vue';
import { dwClickPayNew, dwClickPayRenew, dwClickPayUpgrade } from './workbench-info-dw';
import { dateFormat } from '@vas2t-lowcode/utils';
import { MEMBER_TYPE_CONFIG } from './workbench-info-ui';
import { PRIVILEGE_URL } from '@/constant';
import { getPayConfig, getPositionRule } from '@/utils';

const WorkbenchInfoSkeleton = defineAsyncComponent(() => import('./workbench-info-skeleton.vue'));

const teamStore = usePTeamStore();

const emit = defineEmits<{
  (e: 'set-pay-config-to-pay', data: any): void;
}>();

/** 当前工作区付费信息 */
const teamRights = computed(() => {
  return teamStore.statics?.team_rights;
});

/**
 * @description: 权益名称
 * @return {*}
 */
const memberTitle = computed(() => {
  // 试用版人数取值与其他权益不同
  const cnt = isTrial.value ? teamStore.statics?.team_users?.trial_team_member_cnt : teamRights.value?.cnt;
  return `${cnt}人${teamRights.value?.subject}`;
});

/**
 * @description: 权益到期提示
 * @return {*}
 */
const memberExpire = computed(() => {
  const days = teamRights.value?.expired_near_days || 0;
  const format = dateFormat(teamRights.value?.end_time || 0);
  let str = '';
  if (days > 7) {
    const { trial_days = 30 } = teamRights.value || {};
    const expireFormatStr = `${format} 到期`;
    str = isTrial.value ? `试用${trial_days}天, ${expireFormatStr}` : expireFormatStr;
  } else {
    str = days === 0 ? '今天到期' : `${days}天后到期`;
  }
  return {
    days,
    str,
  };
});

/**
 * @description: 获取当前权益套餐对应配置
 * @return {*}
 */
const memberConfig = computed(() => {
  const memberLevel = teamRights.value?.member_level || 'free';
  return MEMBER_TYPE_CONFIG[memberLevel as keyof typeof MEMBER_TYPE_CONFIG];
});

// 是否为免费版
const isFree = computed(() => {
  return teamRights.value?.member_level === 'free';
});

// 是否为试用版
const isTrial = computed(() => {
  return teamRights.value?.member_level === 'trial';
});

/**
 * 支付函数
 * position 拼接规则: 使用天数 | 平台版本 | 支付位置 | 模板名称 (非轻流内嵌页无需上报)
 */
async function goPay(type: 1 | 2 | 3, position: string) {
  const payConfig = await getPayConfig({
    position: getPositionRule({
      create_time: teamRights.value?.create_time || 0,
      member_level: teamRights.value?.member_level || '',
      position,
    }),
    extra: {
      purchase_type: type,
      team_id: teamStore.statics?.curr_team_info?.team_id,
      cur_member_id: teamRights.value?.member_id,
      cur_duration_expires: teamRights.value?.end_time,
      cur_numeric_value: teamRights.value?.cnt,
    },
  });

  // 埋点
  switch (type) {
    case 1:
      dwClickPayNew();
      break;
    case 2:
      dwClickPayRenew();
      break;
    case 3:
      dwClickPayUpgrade();
      break;
  }

  // 设置支付参数,调起支付
  emit('set-pay-config-to-pay', payConfig);
}

/**
 * @description: 新开 Tab 权益详情
 * @return {*}
 */
const goPrivilegeDetail = () => {
  window.open(PRIVILEGE_URL);
};
</script>

<style lang="scss" scoped>
.left {
  margin-right: 4px;
  &-label {
    font-size: 12px;
    line-height: 22px;
    margin-bottom: 10px;
  }
}

:deep(.buy-btn) {
  width: 64px;
}

:deep(.buy-btn_small) {
  width: 32px;
  padding: 0 !important;
  &.el-button.el-button + .el-button.el-button {
    margin-left: 4px;
  }
}

.right {
  min-width: 195px;
  .tips {
    margin-right: 6px;
  }
}

.pay {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  &-logo {
    width: 24px;
    height: 24px;
    z-index: 2;
  }
  &-text {
    font-size: 11px;
    color: #417ff9;
    line-height: 20px;
    height: 20px;
    padding: 0 9px 0 15px;
    background: #ffffff;
    margin-left: -12px;
    border-radius: 0 100px 100px 0;
    font-size: 11px;
    // vip 样式
    &.pay-text_vip {
      color: #ff980f;
    }
  }
}

.ad-text {
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 22px;
  color: rgba(255, 255, 255, 0.8);
  .privilege-detail {
    cursor: pointer;
    text-decoration: underline;
    margin-left: 5px;
  }
}

.skeleton-main {
  > div:nth-of-type(1) {
    margin-bottom: 9px;
  }
}
</style>
